/**
 * Fellows Corner mentorship form hook.
 *
 * Manages form state, validation, and submission for mentorship inquiries.
 * Reuses the shared createInquiryV2 infrastructure, mirroring the Medical On
 * Demand form: the recipient is resolved from the mentor registry at submit
 * time, so a platform-expert mentor receives a direct conversation while a
 * non-user mentor is relayed through Primum Support.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ERROR_MESSAGES } from '@/constants/errorMessages';
import { FELLOWS_CORNER_CANCER_FOCUS_ID, type MentorTarget } from '@/constants/fellowsCorner';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useToaster } from '@/hooks/useToaster';
import { createInquiryV2 } from '@/services/createInquiryV2';
import { createMentorInquiryMessageData } from '@/types/messageData';
import { segmentClient } from '@/util/analytics';
import { Sentry } from '@/util/sentry';

/** Maximum character length for mentorship inquiry messages. */
const MENTORSHIP_MESSAGE_MAX_LENGTH = 5000;

const mentorshipFormSchema = z.object({
  message: z
    .string()
    .trim()
    .min(1, { message: 'Please enter your question' })
    .max(MENTORSHIP_MESSAGE_MAX_LENGTH, {
      message: `Question must be less than ${MENTORSHIP_MESSAGE_MAX_LENGTH} characters`,
    }),
  anonymous: z.boolean(),
  consentAcknowledged: z.literal(true, {
    errorMap: () => ({ message: 'Please confirm before submitting' }),
  }),
});

export type MentorshipFormData = z.infer<typeof mentorshipFormSchema>;

interface UseMentorshipInquiryFormProps {
  mentor: MentorTarget;
  /**
   * Whether the mentor is currently away. Only changes the success
   * confirmation copy so a fellow who submitted to an away mentor is told to
   * expect a reply on return rather than soon; submission itself is unaffected.
   */
  isAway?: boolean;
  onSuccess?: () => void;
}

export function useMentorshipInquiryForm({
  mentor,
  isAway,
  onSuccess,
}: UseMentorshipInquiryFormProps) {
  const toast = useToaster();

  const [submissionStatus, setSubmissionStatus] = useState<string>('');

  const hasTrackedFormStart = useRef(false);

  const form = useForm<MentorshipFormData>({
    defaultValues: {
      message: '',
      anonymous: false,
      consentAcknowledged: false as unknown as true,
    },
    resolver: zodResolver(mentorshipFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = form;

  const formValues = useWatch({ control });

  useEffect(() => {
    if (!hasTrackedFormStart.current) {
      segmentClient
        .track(ANALYTICS_EVENTS.FELLOWS_MENTOR_FORM_STARTED, { mentor_id: mentor.id })
        ?.catch(() => {});

      hasTrackedFormStart.current = true;
    }
  }, [mentor.id]);

  const createInquiryMutation = useMutation({
    mutationFn: createInquiryV2,
    onSuccess: (_, variables) => {
      setSubmissionStatus('');

      segmentClient
        .track(ANALYTICS_EVENTS.FELLOWS_MENTOR_FORM_SUBMITTED, {
          mentor_id: mentor.id,
          expert_id: variables.expertId,
          anonymous: variables.anonymous,
          is_away: !!isAway,
        })
        ?.catch(() => {});

      toast.showSuccess({
        title: `Your question is on its way to ${mentor.name}`,
        message: isAway
          ? 'Away right now, but will reply as soon as able.'
          : 'Watch your inbox for a reply.',
      });

      reset();

      onSuccess?.();
    },
    onError: (error) => {
      setSubmissionStatus('');

      segmentClient
        .track(ANALYTICS_EVENTS.FELLOWS_MENTOR_FORM_SUBMIT_FAILED, {
          mentor_id: mentor.id,
          error: error instanceof Error ? error.message : 'Unknown error',
        })
        ?.catch(() => {});

      toast.showError({
        title: 'Unable to submit question',
        message:
          'Please check your connection and try again. If the problem persists, contact support.',
      });
    },
  });

  const isMaintenanceMode = useMaintenanceMode();

  const submitInquiry = handleSubmit(async (data) => {
    if (isMaintenanceMode) {
      toast.showError(ERROR_MESSAGES.INQUIRY.MAINTENANCE_MODE);

      return;
    }

    const { recipientExpertId } = mentor;

    if (recipientExpertId === null) {
      toast.showError({
        title: 'Mentor not available yet',
        message: `${mentor.name} isn't accepting questions through Primum yet. Please check back soon.`,
      });

      return;
    }

    try {
      setSubmissionStatus('Submitting question...');

      createInquiryMutation.mutate({
        cancerFocusId: FELLOWS_CORNER_CANCER_FOCUS_ID,
        message: data.message,
        expertIds: [recipientExpertId],
        expertId: recipientExpertId,
        anonymous: data.anonymous,
        anonymousFanOut: false,
        noPII: data.consentAcknowledged,
        data: createMentorInquiryMessageData(),
      });
    } catch (error) {
      setSubmissionStatus('');

      Sentry.captureException(error, {
        tags: { component: 'useMentorshipInquiryForm', source: 'fellows-corner' },
        extra: { mentorId: mentor.id },
      });

      toast.showError({
        title: 'Submission failed',
        message: 'An unexpected error occurred. Please try again.',
      });
    }
  });

  const isSubmitting = createInquiryMutation.isPending || !!submissionStatus;

  const canSubmit = useCallback(
    () => !!formValues.message?.trim() && !!formValues.consentAcknowledged && !isSubmitting,
    [formValues, isSubmitting]
  );

  return {
    control,
    errors,
    formValues,
    setValue,
    submitInquiry,
    isSubmitting,
    submissionStatus,
    canSubmit,
    reset,
  };
}
